/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import "./ArticlePlugin.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import { $dfs } from "@lexical/utils";
import { useDebouncedCallback } from "hooks/useDebounce";
import type { ElementNode, LexicalNode, NodeKey, ParagraphNode, RangeSelection, RootNode, TextNode } from "lexical";
import {
    $createParagraphNode,
    $createTextNode,
    $getPreviousSelection,
    $getRoot,
    $getSelection,
    $isElementNode,
    $isNodeSelection,
    $isParagraphNode,
    $isRangeSelection,
    $isRootNode,
    $isTextNode,
    $setSelection,
    COMMAND_PRIORITY_LOW,
    createCommand,
    DELETE_CHARACTER_COMMAND,
    INSERT_PARAGRAPH_COMMAND,
    NodeSelection,
    SELECTION_CHANGE_COMMAND,
} from "lexical";
import { useEffect } from "react";

import { ConditionalNode } from "../ConditionalNode";
import { getLetterNumbering, hasLetterNumbering } from "../utils";
import { isContractSettingsPage, isContractValidationPage, isSetupPage } from "./../../../../helpers/utils";
import i18n from "./../../../../i18n";
import { $createArticleContainerNode, $isArticleContainerNode, ArticleContainerNode } from "./ArticleContainerNode";
import { $createArticleContentNode, $isArticleContentNode, ArticleContentNode } from "./ArticleContentNode";
import { $createArticleTitleNode, $isArticleTitleNode, ArticleTitleNode } from "./ArticleTitleNode";

export const INSERT_ARTICLE_COMMAND = createCommand<{ getCurrentSelection: boolean }>();
export const TOGGLE_ARTICLE_COMMAND = createCommand<NodeKey>();

type ArticleNodeList = { depth: number; nodes: ArticleContainerNode[] };

/**
 * This function recalculates the articles indexes, and if the readOnly boolean is set to false, adds an article
 * @param { boolean } readOnly Will only recalculate without adding an article
 * @param { boolean } getCurrentSelection Will choose if it needs the previous selection or current
 * @param { "numerical" | "physical" } numbering type a) b) c) or 1 2 3
 */
export const recalculateNumberingArticles = (
    readOnly: boolean = false,
    getCurrentSelection: boolean = false,
    numberingType: "numerical" | "alphabetical" = "numerical",
    isSwitchingNumbering: boolean = false,
) => {
    let selection = getCurrentSelection ? $getSelection() : $getPreviousSelection();
    const _selectionNode = (selection as RangeSelection)?.anchor.getNode();
    let selectionNode = _selectionNode;
    // As we can add an article on an empty line (with spaces but no char) -> textNode,
    // we always need start from the parent -> paragraphNode
    // We delete the current empty textNode or it will cause issues on the numbering calculation
    if ($isTextNode(_selectionNode)) {
        const parentNode = _selectionNode.getParent<ElementNode>();
        if (parentNode && $isParagraphNode(parentNode)) {
            selectionNode = parentNode;
            parentNode.select();
            _selectionNode.remove();
            selection = $getSelection();
        }
    }

    /**
     * problem of $nodesOfType is that it returns all the nodes ordered by key
     * (means last added element at the bottom of the array, even tho we put it on the first line)
     * not by actual position in the editor
     */
    // const articleNodes = $nodesOfType(ArticleContainerNode);
    const index = [0, 0, 0];
    let isArticleAtRootLevel;

    // checks if the node is a article title node and if so, returns (adding article from there is bugged)
    if ($isRangeSelection(selection) && !readOnly) {
        const currentNode = selection.anchor.getNode();
        isArticleAtRootLevel = $isRootNode(currentNode?.getParent());
        if ($isParagraphNode(currentNode?.getParent()))
            isArticleAtRootLevel = $isRootNode(currentNode?.getParent()?.getParent());
        // removing the depth check because it causes problems when adding article from a selected text, no more limit
        if ($isArticleTitleNode(selection.anchor.getNode()?.getParent<ElementNode>()?.getParent<ElementNode>())) {
            return false;
        }
    }
    let selectedText = "";
    if ($isRangeSelection(selection) && !selection.isCollapsed() && !readOnly)
        selectedText = selection.getTextContent();
    const title = $createArticleTitleNode(true);
    const paragraph = $createParagraphNode();
    const text = $createTextNode(isArticleAtRootLevel ? "Article " : "").setFormat("bold");

    /**
     *
     * creates and inserts the article container node into the editor
     * an article container is composed of:
     * - article-container
     *   - article-title
     *     - paragraph
     *       - text
     *   - article-content
     *     - paragraph
     */
    const newArticleNode = $createArticleContainerNode(true, index, "", true, false).append(
        title.append(paragraph.append(...[text])),
        $createArticleContentNode().append($createParagraphNode().append($createTextNode(selectedText))),
    );

    // find if the parent node is either a root node or an article content node
    let parentNode: ArticleContainerNode | ArticleContentNode | RootNode | null | undefined;
    if ($isRangeSelection(selection)) {
        parentNode = selection.anchor.getNode().getParent<ArticleContentNode | RootNode>();
    }
    if (parentNode && $isArticleContentNode(parentNode)) {
        parentNode = parentNode.getParent<ArticleContainerNode>();
    }
    const key = parentNode?.getKey();
    if (!readOnly) {
        if (selection && $isRangeSelection(selection)) {
            const currentNode = selection.anchor.getNode();
            currentNode.replace(newArticleNode);
            newArticleNode.getLastChild<ArticleContentNode>()?.select();

            // only render a linebreak if there is no paragraph before or after ArticleNode
            if (newArticleNode.getNextSibling()?.__type !== "paragraph" || !getCurrentSelection) {
                newArticleNode.insertAfter($createParagraphNode());
            }
            if (newArticleNode.getPreviousSibling()?.__type !== "paragraph" || !getCurrentSelection) {
                newArticleNode.insertBefore($createParagraphNode());
            }
        }
    }

    // get all nodes on the editor
    const allNodes = $dfs();
    // filter the nodes to get only the article nodes
    const allArticleNodes: { depth: number; node: ArticleContainerNode }[] = allNodes.filter((node) =>
        $isArticleContainerNode(node.node),
    ) as unknown as { depth: number; node: ArticleContainerNode }[];

    if ($isRangeSelection(selection)) {
        /**
         * group the article nodes by their parent
         * ex: {
         *  "root": {{...article, nodes: [...element, {key: 144}]}, {...article}},
         *  "144": {depth: 3, nodes: [...element]} // the node here is a child of the above node with key "144"
         * }
         * in this example we have 2 article nodes at the root level and 1 article node inside the article node with key "144"
         */
        const groupedArticleNodes = allArticleNodes.reduce(
            (acc, node) => {
                // save the parent node and potentially the parent if it's an article content node
                let parent1: ArticleContainerNode | ArticleContentNode | RootNode | null = node.node.getParent<
                    ArticleContentNode | RootNode
                >();

                // if the parent1 is not one of the possible types, cancel
                // if (!parent1 || (!$isArticleContentNode(parent1) && !$isParagraphNode(parent1) && !$isRootNode(parent1))) {
                //     return acc;
                // }

                // if the parent exists and is an article content node, get it's parent
                if (parent1 && $isArticleContentNode(parent1)) parent1 = parent1.getParent<ArticleContainerNode>();
                if (parent1 && $isParagraphNode(parent1) && $isRootNode(parent1.getParent()))
                    parent1 = parent1.getParent<ArticleContainerNode>();
                if (parent1 && $isParagraphNode(parent1) && $isArticleContentNode(parent1.getParent()))
                    parent1 = parent1.getParent<ArticleContainerNode>()!.getParent<ArticleContainerNode>();

                if (parent1) {
                    // create a key for the parent node and appends the node key if the parent is an article content node
                    const key = parent1.getKey();

                    if (!acc[key]) {
                        acc[key] = {
                            depth: node.depth,
                            nodes: [],
                        };
                    }
                    acc[key].nodes.push(node.node);
                }
                return acc;
            },
            {} as Record<string, ArticleNodeList>,
        );

        const incrementArticleIndex = (currentNode: ArticleNodeList, prefix: number[] = []) => {
            // Get selection siblings when switching numbering type
            let articleSiblings: ArticleContainerNode[] | LexicalNode[] = [];

            if (!readOnly) {
                articleSiblings = [
                    ...newArticleNode.getPreviousSiblings(),
                    ...newArticleNode.getNextSiblings(),
                    newArticleNode,
                ].filter((node) => $isArticleContainerNode(node));
            } else {
                articleSiblings = [
                    ...selectionNode.getPreviousSiblings(),
                    ...selectionNode.getNextSiblings(),
                    selectionNode,
                ].filter((node) => $isArticleContainerNode(node));
            }

            // Here we increment article numbering + format the name of articles
            let numberToAdd = 1;
            // Only get the title with this regex
            // Article title can start with "Article 1, Article 2.. 1.2, 1.3 .. or a), b), c).."
            const cleanTitleRegex = /^\d+(\.\d+)*\s*\.\s*|\bArticle\s+\d+\.\s*|[a-z]\)\s*|\d+(\.\d+)*\s*[.)]*\s*/;

            for (const [i, node] of currentNode.nodes.entries()) {
                /* Check if the node is a sibling of the current selection Node
                 ** If switching numbering, Only change numbering of current article and it siblings.
                 */
                if (isSetupPage()) {
                    articleSiblings = [...node.getPreviousSiblings(), ...node.getNextSiblings(), node].filter((node) =>
                        $isArticleContainerNode(node),
                    );
                }
                const isSiblingArticle = articleSiblings.some((siblingNode) => siblingNode.__key === node.__key);

                const titleNode = node
                    .getFirstChild<ArticleTitleNode>()
                    ?.getFirstChild<ParagraphNode>()
                    ?.getFirstChild<TextNode>();

                let fullArticleTitle = titleNode?.__text || "";
                const usesLetterNumbering = hasLetterNumbering(fullArticleTitle);
                const hasOptionalTag = fullArticleTitle.includes(`(${i18n.t("RichEditor.Optional")})`);
                // Get Title of sibling article containers
                const articleSiblingTitles = (articleSiblings as ArticleContainerNode[]).map(
                    (siblingNode) =>
                        siblingNode
                            .getChildren()
                            .find((node) => $isArticleTitleNode(node))
                            ?.getTextContent(),
                );
                const hasSiblingLetterNumbering = articleSiblingTitles.some((title) => hasLetterNumbering(title ?? ""));

                if (hasOptionalTag)
                    fullArticleTitle = fullArticleTitle.replace(`(${i18n.t("RichEditor.Optional")})`, "");

                let articleTitle = fullArticleTitle.replace(cleanTitleRegex, "").trim();
                if (articleTitle === "Article") articleTitle = "";

                let isArticleAtRootLevel = $isRootNode(node?.getParent());
                if ($isParagraphNode(node?.getParent()))
                    isArticleAtRootLevel = $isRootNode(node?.getParent()?.getParent());
                let numbering = [...prefix, i + numberToAdd];
                const articleName = isArticleAtRootLevel ? "Article " : "";

                /**
                 * Handle Article Title when adding optional (display full article title)
                 */
                const isCreatingOptionalArticle = isSiblingArticle && !isSwitchingNumbering && isContractSettingsPage();

                if (isCreatingOptionalArticle || isContractValidationPage() || isSetupPage()) {
                    if ((!isSetupPage() && node.getConditional()) || (isSetupPage() && !node.getShow())) {
                        if (isContractValidationPage()) continue;
                        if (isSetupPage()) numberToAdd -= 1;
                        numbering = [...prefix, i + numberToAdd];
                        const isOptional = fullArticleTitle.includes(`(${i18n.t("RichEditor.Optional")})`);

                        if (hasSiblingLetterNumbering) {
                            titleNode?.setTextContent(
                                `${!isOptional ? `(${i18n.t("RichEditor.Optional")})` : ""} ${
                                    !node.getShow || !isSetupPage()
                                        ? `${getLetterNumbering(numbering)}) ${articleTitle}`
                                        : articleTitle
                                }`,
                            );
                        } else {
                            titleNode?.setTextContent(
                                `${!isOptional ? `(${i18n.t("RichEditor.Optional")})` : ""} ${
                                    !node.getShow || !isSetupPage()
                                        ? `${articleName}${numbering.join(".")}. ${articleTitle}`
                                        : articleTitle
                                }`,
                            );
                        }
                        continue;
                    }
                }

                node.setIndex(numbering);

                if ((!isSwitchingNumbering && isSiblingArticle) || isSetupPage()) {
                    if (hasSiblingLetterNumbering || usesLetterNumbering)
                        titleNode?.setTextContent(`${getLetterNumbering(numbering)})  ${articleTitle}`);
                    else titleNode?.setTextContent(`${articleName}${numbering.join(".")}.  ${articleTitle}`);
                }
                if (isSwitchingNumbering && isSiblingArticle) {
                    numberingType === "numerical" &&
                        titleNode?.setTextContent(
                            `${
                                hasOptionalTag ? `(${i18n.t("RichEditor.Optional")})` : ""
                            } ${articleName}${numbering.join(".")}.  ${articleTitle}`,
                        );
                    numberingType === "alphabetical" &&
                        titleNode?.setTextContent(
                            `${hasOptionalTag ? `(${i18n.t("RichEditor.Optional")})` : ""} ${getLetterNumbering(
                                numbering,
                            )})  ${articleTitle}`,
                        );
                }

                if (node.getKey() in groupedArticleNodes) {
                    incrementArticleIndex(groupedArticleNodes[node.getKey()], numbering);
                }
            }
        };

        /**
         * loop through the grouped article nodes and update the index of the article nodes
         * if it is superior to the index of the new article node and update the text of the article title
         */
        if (key) {
            incrementArticleIndex(groupedArticleNodes.root);
        }
    }
    if (!readOnly) paragraph.select();
};

export default function ArticlePlugin(): null {
    const [editor] = useLexicalComposerContext();
    const debounce = useDebouncedCallback(() => {
        editor.setEditable(true);
        setTimeout(() => {
            editor.focus();
        }, 100);
    }, 200);

    useEffect(() => {
        if (!editor.hasNodes([ArticleContainerNode, ArticleTitleNode, ArticleContentNode])) {
            throw new Error(
                "ArticlePlugin: ArticleContainerNode, ArticleTitleNode, or ArticleContentNode not registered on editor",
            );
        }
        const onEscapeUp = () => {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed() && selection.anchor.offset === 0) {
                const container = $findMatchingParent(selection.anchor.getNode(), $isArticleContainerNode);

                if ($isArticleContainerNode(container)) {
                    const parent = container.getParent<ElementNode>();
                    if (
                        parent !== null &&
                        parent.getFirstChild<LexicalNode>() === container &&
                        selection.anchor.key === container.getFirstDescendant<LexicalNode>()?.getKey()
                    ) {
                        container.insertBefore($createParagraphNode());
                    }
                }
            }

            return false;
        };

        const onEscapeDown = () => {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed()) {
                const container = $findMatchingParent(selection.anchor.getNode(), $isArticleContainerNode);

                if ($isArticleContainerNode(container)) {
                    const parent = container.getParent<ElementNode>();
                    if (parent !== null && parent.getLastChild<LexicalNode>() === container) {
                        const titleParagraph = container.getFirstDescendant<LexicalNode>();
                        const contentParagraph = container.getLastDescendant<LexicalNode>();

                        if (
                            (contentParagraph !== null &&
                                selection.anchor.key === contentParagraph.getKey() &&
                                selection.anchor.offset === contentParagraph.getTextContentSize()) ||
                            (titleParagraph !== null &&
                                selection.anchor.key === titleParagraph.getKey() &&
                                selection.anchor.offset === titleParagraph.getTextContentSize())
                        ) {
                            container.insertAfter($createParagraphNode());
                        }
                    }
                }
            }

            return false;
        };

        const recalculateNumbering = (readOnly = false, getCurrentSelection = false) => {
            editor.update(() => {
                recalculateNumberingArticles(readOnly, getCurrentSelection);
            });
            return true;
        };

        return mergeRegister(
            // Structure enforcing transformers for each node type. In case nesting structure is not
            // "Container > Title + Content" it'll unwrap nodes and convert it back
            // to regular content.
            editor.registerNodeTransform(ArticleContentNode, (node) => {
                const parent = node.getParent<ElementNode>();
                if (!$isArticleContainerNode(parent)) {
                    const children = node.getChildren<LexicalNode>();
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),

            editor.registerNodeTransform(ArticleTitleNode, (node) => {
                const parent = node.getParent<ElementNode>();
                if (!$isArticleContainerNode(parent)) {
                    node.replace($createParagraphNode().append(...node.getChildren<LexicalNode>()));
                    return;
                }
            }),

            editor.registerNodeTransform(ArticleContainerNode, (node) => {
                if (node.getConditional() && !node.getShow() && isContractValidationPage()) {
                    node.remove();
                }
                const children = node.getChildren<LexicalNode>();
                if (children.length !== 2 || !$isArticleTitleNode(children[0]) || !$isArticleContentNode(children[1])) {
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),

            editor.registerNodeTransform(ConditionalNode, (node) => {
                if (!node.getShow() && isContractValidationPage()) {
                    node.remove();
                }
            }),

            editor.registerMutationListener(ArticleContainerNode, (mutatedNodes) => {
                // mutatedNodes is a Map where each key is the NodeKey, and the value is the state of mutation.
                for (const [nodeKey, mutation] of mutatedNodes) {
                    if (mutation == "destroyed") recalculateNumbering(true);
                }
            }),

            // This handles the case when container is collapsed and we delete its previous sibling
            // into it, it would cause collapsed content deleted (since it's display: none, and selection
            // swallows it when deletes single char). Instead we expand container, which is although
            // not perfect, but avoids bigger problem
            editor.registerCommand(
                DELETE_CHARACTER_COMMAND,
                () => {
                    const selection = $getSelection();
                    // if (!$isRangeSelection(selection) || !selection.isCollapsed() || selection.anchor.offset !== 0) {
                    //     return false;
                    // }
                    const anchorNode = (selection as any).anchor.getNode() as TextNode;
                    const topLevelElement = anchorNode.getTopLevelElement();
                    const articleContainer = anchorNode
                        .getParents()
                        .find((parent) => parent.__type === "article-container");
                    if (topLevelElement === null) {
                        return false;
                    }
                    const articleTitleNode = anchorNode.getParents().find((node) => node.__type === "article-title");
                    const container = topLevelElement.getPreviousSibling<LexicalNode>();

                    // Remove the whole article if article title is removed
                    if (articleTitleNode && articleContainer) {
                        if (anchorNode.__text?.length <= 1) {
                            const children = topLevelElement.getChildren<LexicalNode>();
                            for (const child of children) {
                                topLevelElement.insertBefore(child);
                            }
                            topLevelElement.remove();
                        }
                    }
                    /**
                     * After delete one article then add immediatly one again.
                     * It looks like topLevelElement doesn't get the article-container
                     * So I need to get and remove it manually
                     */
                    if (articleTitleNode && articleContainer) {
                        if (anchorNode.__text?.length <= 1) {
                            const children = articleContainer.getChildren<LexicalNode>();
                            for (const child of children) {
                                articleContainer.insertBefore(child);
                            }
                            articleContainer.remove();
                        }
                    }

                    if (!$isArticleContainerNode(container) || container.getOpen()) {
                        return false;
                    }

                    container.setOpen(true);
                    return true;
                },
                COMMAND_PRIORITY_LOW,
            ),

            // When collapsible article is the last child pressing down/right arrow will insert paragraph
            // below it to allow adding more content. It's similar what $insertBlockNode
            // (mainly for decorators), except it'll always be possible to continue adding
            // new content even if trailing paragraph is accidentally deleted
            // editor.registerCommand(KEY_ARROW_DOWN_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

            // editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

            // When collapsible article is the first child pressing up/left arrow will insert paragraph
            // above it to allow adding more content. It's similar what $insertBlockNode
            // (mainly for decorators), except it'll always be possible to continue adding
            // new content even if leading paragraph is accidentally deleted
            // editor.registerCommand(KEY_ARROW_UP_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

            // editor.registerCommand(KEY_ARROW_LEFT_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

            // Prevents adding a new line in the article's title

            editor.registerCommand(
                INSERT_PARAGRAPH_COMMAND,
                () => {
                    const windowEvent = editor._window?.event as KeyboardEvent | undefined;

                    if (windowEvent && windowEvent.key === "Enter") {
                        const selection = $getPreviousSelection();
                        if ($isRangeSelection(selection) && selection.isCollapsed()) {
                            const parent = $findMatchingParent(
                                selection.anchor.getNode(),
                                (node) => $isElementNode(node) && !node.isInline(),
                            );
                            if ($isArticleTitleNode(parent?.getParent())) {
                                const container = parent.getParent<ElementNode>()?.getParent<ElementNode>();
                                if ($isArticleContainerNode(container)) {
                                    // container.toggleOpen();
                                    // $setSelection(selection.clone());
                                    container.getLastChild()?.selectEnd().toggleFormat("bold");
                                    return true;
                                }
                            }
                        }
                    }

                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                INSERT_ARTICLE_COMMAND,
                ({ getCurrentSelection = false }) => recalculateNumbering(false, getCurrentSelection),
                COMMAND_PRIORITY_LOW,
            ),

            // this command permits selection on details tag aka the articles
            // it works by setting the editor to read only when a selection is made
            // and then debouncing the editor to be editable again 500ms after
            // the reason is that when the details tag has contentEditable set to true
            // the selection doesn't work properly, it's a bug from html/browser
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                (payload) => {
                    const selection = $getSelection();
                    if (
                        ($isRangeSelection(selection) && selection.isCollapsed()) ||
                        isSetupPage() ||
                        isContractValidationPage() ||
                        $isNodeSelection(selection)
                    )
                        return false;
                    editor.setEditable(false);
                    debounce();
                    return true;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor]);

    return null;
}
